import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { MATCAP_GAIN, MATCAP_DARK, BODY_MAX, RIM_MAX, BOX_LUM, LUMW, lumMaxSousRotation, teinte, smooth01, studio } from './lib/matcap.js';
import { vdjColor, vdjBeatChroma, vdjLoop, vdjCameraKick, VDJ_MIRROR_CHUNK, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière est DANS la matcap.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'totem',
  family: 'object',
  danceability: 0.85,
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 110,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.60,
  enabled: true
};

// Totem : des sculptures 3D chromées et iridescentes en orbite autour de la
// pochette, qui se transforment sur la mesure et se reflètent dans un fond
// kaléidoscopique. LOOK VDJ appliqué (lib/vdjLook.js) : noir pur, néon
// saturé, coupes sur la mesure, boucles de 8 et 16 temps, séparation RGB sur
// le temps par l'aberration de la passe filmic, miroir changé sur les
// phrases, à-coup de caméra sur la mesure, flash de drop à 0,85 sous le
// budget de confort.
//
// LES SCULPTURES (une géométrie par forme, construites une fois) :
//   KNOT   noeud de tore (2,3) ou (3,5)
//   TWIST  tore à section quadrilobée, vrillée dans le vertex shader
//   STACK  cinq tores décalés et inclinés, chacun en précession
//   BLOB   icosphère déformée par trois sinus (position ET normale analytiques)
//   SPIKES icosphère dont chaque face porte une pointe qui sort sur le temps
// Chaque sommet porte aSphere, SA place sur la sphère unité (longitude et
// latitude de la paramétrisation du tube ; la direction pour les icosphères).
// Le morph de la mesure interpole position et normale vers aSphere : toute
// forme contractée est LA MÊME sphère, le changement de géométrie se fait à
// ce moment-là, sans déchirure. NE PAS prendre normalize(position) pour un
// tore : l'axe du trou n'est jamais atteint et la sphère aurait deux trous
// aux pôles.
//
// LA LUMIÈRE : une matcap 256 x 256 générée à l'arrivée de la pochette, trois
// matériaux (CHROME, IRIDESCENT, NEON) : un studio avec deux bandes blanches
// nettes au-dessus du seuil de bloom, un liseré de Fresnel au bord, et pour
// l'iridescent une palette cosinus en fonction de l'angle de vue décalée par
// la teinte de la pochette. UNE lecture de texture par fragment ; la teinte
// tourne ensuite dans le shader (rotation RGB autour de l'axe gris : le blanc
// reste blanc) avec un gradient le long de l'objet : les couleurs coulent sur
// la surface sans que l'objet bouge.
//
// LE FOND : les mêmes sculptures rendues une deuxième fois dans une cible à
// résolution réduite, lues par le kaléidoscope du look VDJ, agrandies et
// assombries à 35 %. Le plancher de la zone sûre (0,5) ne s'applique qu'à
// ce fond : les orbites évitent le centre par construction.

export const SHAPES = ['knot23', 'knot35', 'twist', 'stack', 'blob', 'spikes'];
const SHAPE_ID = { knot23: 0, knot35: 0, twist: 1, stack: 2, blob: 3, spikes: 4 };
export const MATCAPS = ['chrome', 'iridescent', 'neon'];
export const MATCAP_SIZE = 256;

const TIER = {
  0: { sculptures: 2, bgScale: 0 },
  1: { sculptures: 3, bgScale: 0.5 },
  2: { sculptures: 3, bgScale: 0.5 }
};
// LA COMPOSITION (voir orbite()) : rayon englobant d'une sculpture (pointes
// sorties comprises, saut du temps x1,144), marge de 5 % de la hauteur, plus
// ce que l'à-coup de caméra (+4 %, +-3 degrés) et la dérive peuvent ajouter
const OBJ_R = 1.5 * 1.144, MARGIN_NDC = 0.10, KICK_NDC = 0.04, KICK_ZOOM_MAX = 1.04;
const ORBIT_DEPTH = 1.2, GROUP_Y = 1.35;
const ORBIT_BEATS = 16, SPIN_BEATS = 8, WOBBLE_BEATS = 16;
const SPHERE_R = 0.9;             // la sphère du morph
const MESH_SCALE = 1.0;
const BEAT_JUMP = 0.12, BEAT_TAU_S = 0.12;
const SPIKE_LEN = 0.55, SPIKE_TAU_S = 0.16;
const BG_ZOOM = 1.6, BG_DARK = 0.35, BG_SAFE_FLOOR = 0.5;
const BG_TURN_BEATS = 32;                 // la rosace tourne en 32 temps
const BG_PHRASE_TURN = Math.PI / 6;       // et saute de 30 degrés sur la phrase (coupe)
// LE FOND EN ROSACE. Le kaléidoscope ne lit qu'un secteur de 60 degrés autour
// du centre. Une sculpture posée AU MILIEU du secteur s'y reflète en six
// copies isolées - ce qu'il fallait éviter. Pour la passe du fond, elles sont
// donc rapprochées du centre (rayon BG_R) et agrandies (BG_S) : à ce rayon,
// l'arc du secteur (BG_R x pi/3 = 0,9) est bien plus étroit que l'objet
// (2 x 1,3 x 1,4 = 3,6), chaque sculpture chevauche TOUJOURS une ligne de
// pliure ET le centre : les pétales se soudent en une seule rosace (à 1,5,
// on voyait trois pétales séparés), jamais un objet entier reconnaissable.
const BG_R = 0.9, BG_R_SWING = 0.3, BG_S = 1.4;
const DROP_BARS = 4, DROP_SPIN = 2.2;
const RISE_SPIN = 1.4;
const PAUSE_RATE = 0.1;
const HUE_BASE = 0.04, HUE_HIGH = 0.45;    // tours par seconde
const HUE_GRAD = 0.12;                      // tours par unité de hauteur locale
const EXPOSURE = 1.0;
const LUM_EVERY = 30;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// ---------------------------------------------------------------- géométries

function setAttr(geom, name, arr, size) { geom.setAttribute(name, new THREE.BufferAttribute(arr, size)); }

// Un tube autour d'une courbe fermée : segU anneaux le long, segV autour.
// aSphere : longitude = u, latitude = v (bande [lat0, lat1] de la sphère).
function tube(curve, segU, segV, radius, lat0, lat1, paramOf) {
  const nv = (segU + 1) * (segV + 1);
  const pos = new Float32Array(nv * 3), nor = new Float32Array(nv * 3), sph = new Float32Array(nv * 3), par = new Float32Array(nv * 2);
  const idx = [];
  const P = new THREE.Vector3(), P1 = new THREE.Vector3(), P2 = new THREE.Vector3(), T = new THREE.Vector3(), N = new THREE.Vector3(), B = new THREE.Vector3(), D = new THREE.Vector3();
  let k = 0;
  for (let i = 0; i <= segU; i++) {
    const u = (i % segU) / segU, t = u * Math.PI * 2;
    curve(t, P); curve(t + 1e-3, P1); curve(t - 1e-3, P2);
    T.subVectors(P1, P2).normalize();
    N.copy(P).normalize(); N.crossVectors(T, N).normalize();   // T x P̂ : stable tant que la courbe n'est pas radiale
    B.crossVectors(T, N).normalize();
    const lon = u * Math.PI * 2;
    for (let j = 0; j <= segV; j++) {
      const v = j / segV, ph = v * Math.PI * 2;
      D.copy(N).multiplyScalar(Math.cos(ph)).addScaledVector(B, Math.sin(ph));
      pos[k * 3] = P.x + D.x * radius; pos[k * 3 + 1] = P.y + D.y * radius; pos[k * 3 + 2] = P.z + D.z * radius;
      nor[k * 3] = D.x; nor[k * 3 + 1] = D.y; nor[k * 3 + 2] = D.z;
      const lat = lat0 + (lat1 - lat0) * v, cl = Math.cos(lat);
      sph[k * 3] = Math.cos(lon) * cl; sph[k * 3 + 1] = Math.sin(lat); sph[k * 3 + 2] = Math.sin(lon) * cl;
      const pp = paramOf ? paramOf(t, ph) : [t, ph];
      par[k * 2] = pp[0]; par[k * 2 + 1] = pp[1];
      k++;
    }
  }
  // L'ORDRE DES SOMMETS : (a, a+1, b) et non (a, b, a+1). Avec (T, D) pour
  // repère, T x dD/dph pointe VERS L'INTÉRIEUR du tube ; dans l'autre sens,
  // le tube était rendu par ses faces arrière, dont les normales tournent le
  // dos à la caméra et se rabattaient toutes sur l'anneau de la matcap : des
  // sculptures blanches et plates. Ce même ordre rend la sphère (lon, lat)
  // du morph vers l'extérieur elle aussi.
  for (let i = 0; i < segU; i++) for (let j = 0; j < segV; j++) {
    const a = i * (segV + 1) + j, b = a + segV + 1;
    idx.push(a, a + 1, b, b, a + 1, b + 1);
  }
  const g = new THREE.BufferGeometry();
  setAttr(g, 'position', pos, 3); setAttr(g, 'normal', nor, 3); setAttr(g, 'aSphere', sph, 3); setAttr(g, 'aParam', par, 2);
  g.setIndex(idx);
  return g;
}

function torusKnotCurve(p, q, R, r) {
  return function (t, out) {
    const cq = Math.cos(q * t), sq = Math.sin(q * t), cp = Math.cos(p * t), sp = Math.sin(p * t);
    const rad = R + r * cq;
    out.set(rad * cp, r * sq, rad * sp);
  };
}

function knot(p, q, R, r, tubeR, segU, segV) {
  return tube(torusKnotCurve(p, q, R, r), segU, segV, tubeR, -Math.PI / 2, Math.PI / 2, null);
}

// Le tore vrillé : positions et normales recalculées dans le vertex shader à
// partir de aParam = (theta, phi) ; les attributs portent le tore lisse.
function twist() {
  const g = tube((t, out) => out.set(0.85 * Math.cos(t), 0, 0.85 * Math.sin(t)), 160, 40, 0.30, -Math.PI / 2, Math.PI / 2, null);
  return g;
}

// Cinq tores empilés, inclinés : chacun occupe une bande de latitude de la
// sphère du morph. aParam.x = indice de l'anneau (précession dans le shader).
function stack() {
  const parts = [];
  const tilt = new THREE.Matrix4(), e = new THREE.Euler();
  for (let i = 0; i < 5; i++) {
    const c = i - 2, R = 0.85 - 0.10 * Math.abs(c), y = c * 0.34;
    const g = tube((t, out) => out.set(R * Math.cos(t), y, R * Math.sin(t)), 72, 14, 0.14, -Math.PI / 2 + i * Math.PI / 5, -Math.PI / 2 + (i + 1) * Math.PI / 5, (t, ph) => [i, t]);
    e.set(c * 0.18, 0, c * 0.07); tilt.makeRotationFromEuler(e);
    // l'inclinaison autour du centre de l'anneau, pas de l'origine
    const pos = g.attributes.position, nor = g.attributes.normal, v = new THREE.Vector3();
    for (let k = 0; k < pos.count; k++) {
      v.fromBufferAttribute(pos, k); v.y -= y; v.applyMatrix4(tilt); v.y += y; pos.setXYZ(k, v.x, v.y, v.z);
      v.fromBufferAttribute(nor, k); v.applyMatrix4(tilt); nor.setXYZ(k, v.x, v.y, v.z);
    }
    parts.push(g);
  }
  // fusion à la main (mêmes attributs partout)
  const names = ['position', 'normal', 'aSphere', 'aParam'];
  const g = new THREE.BufferGeometry();
  for (const n of names) {
    const size = parts[0].attributes[n].itemSize;
    const total = parts.reduce((s, p) => s + p.attributes[n].count, 0);
    const arr = new Float32Array(total * size);
    let off = 0;
    for (const p of parts) { arr.set(p.attributes[n].array, off); off += p.attributes[n].array.length; }
    setAttr(g, n, arr, size);
  }
  const idx = []; let base = 0;
  for (const p of parts) { const ia = p.index.array; for (let k = 0; k < ia.length; k++) idx.push(ia[k] + base); base += p.attributes.position.count; }
  g.setIndex(idx);
  return g;
}

// IcosahedronGeometry duplique chaque sommet par face ; on les fusionne à la
// main (BufferGeometryUtils n'est pas dans ce bundle) pour des normales
// lisses et des faces partagées - les pointes ont besoin des deux.
function icosphere(detail) {
  const raw = new THREE.IcosahedronGeometry(1, detail);
  const pa = raw.attributes.position.array, nr = raw.attributes.position.count;
  const keyOf = new Map(), remap = new Uint32Array(nr), pos = [];
  for (let k = 0; k < nr; k++) {
    const key = Math.round(pa[k * 3] * 1e4) + ',' + Math.round(pa[k * 3 + 1] * 1e4) + ',' + Math.round(pa[k * 3 + 2] * 1e4);
    let id = keyOf.get(key);
    if (id == null) { id = pos.length / 3; keyOf.set(key, id); pos.push(pa[k * 3], pa[k * 3 + 1], pa[k * 3 + 2]); }
    remap[k] = id;
  }
  raw.dispose();
  const g = new THREE.BufferGeometry();
  setAttr(g, 'position', new Float32Array(pos), 3);
  setAttr(g, 'normal', new Float32Array(pos.length), 3);
  g.setIndex(Array.from(remap));
  const n = g.attributes.position.count, sph = new Float32Array(n * 3), par = new Float32Array(n * 2);
  const v = new THREE.Vector3();
  for (let k = 0; k < n; k++) { v.fromBufferAttribute(g.attributes.position, k).normalize(); sph[k * 3] = v.x; sph[k * 3 + 1] = v.y; sph[k * 3 + 2] = v.z; g.attributes.normal.setXYZ(k, v.x, v.y, v.z); }
  setAttr(g, 'aSphere', sph, 3); setAttr(g, 'aParam', par, 2);
  if (g.attributes.uv) g.deleteAttribute('uv');
  return g;
}

// L'icosphère à pointes : chaque face est remplacée par trois triangles qui
// se rejoignent sur une pointe posée au centre de la face (aParam.x = 1) ;
// au repos la pointe est dans le plan de la face, sur le temps elle sort le
// long de sa normale.
function spikes() {
  const base = icosphere(7);                 // 642 sommets, 1 280 faces
  const pos = base.attributes.position, idx = base.index.array, nb = pos.count, nf = idx.length / 3;
  const n = nb + nf;
  const P = new Float32Array(n * 3), N = new Float32Array(n * 3), S = new Float32Array(n * 3), A = new Float32Array(n * 2);
  P.set(pos.array); N.set(base.attributes.normal.array); S.set(base.attributes.aSphere.array);
  const out = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), m = new THREE.Vector3();
  for (let f = 0; f < nf; f++) {
    const i0 = idx[f * 3], i1 = idx[f * 3 + 1], i2 = idx[f * 3 + 2];
    a.fromBufferAttribute(pos, i0); b.fromBufferAttribute(pos, i1); c.fromBufferAttribute(pos, i2);
    m.addVectors(a, b).add(c).multiplyScalar(1 / 3);
    const k = nb + f, dir = m.clone().normalize();
    P[k * 3] = m.x; P[k * 3 + 1] = m.y; P[k * 3 + 2] = m.z;
    N[k * 3] = dir.x; N[k * 3 + 1] = dir.y; N[k * 3 + 2] = dir.z;
    S[k * 3] = dir.x; S[k * 3 + 1] = dir.y; S[k * 3 + 2] = dir.z;
    A[k * 2] = 1;
    out.push(i0, i1, k, i1, i2, k, i2, i0, k);
  }
  base.dispose();
  const g = new THREE.BufferGeometry();
  setAttr(g, 'position', P, 3); setAttr(g, 'normal', N, 3); setAttr(g, 'aSphere', S, 3); setAttr(g, 'aParam', A, 2);
  g.setIndex(out);
  return g;
}

export function construireGeometrie(shape) {
  switch (shape) {
    case 'knot23': return knot(2, 3, 0.68, 0.28, 0.20, 240, 22);
    case 'knot35': return knot(3, 5, 0.70, 0.24, 0.16, 320, 18);
    case 'twist': return twist();
    case 'stack': return stack();
    case 'blob': return icosphere(15);        // three subdivise en (detail+1)^2 : 2 562 sommets
    case 'spikes': return spikes();
    default: throw new Error('forme inconnue ' + shape);
  }
}

const GEOMETRIES = new Map();
function geometrie(shape) {
  let g = GEOMETRIES.get(shape);
  if (!g) { g = construireGeometrie(shape); GEOMETRIES.set(shape, g); }
  return g;
}

// ------------------------------------------------------------------ matcaps

// LES MATCAPS : des boules de chrome photographiées, pas des dégradés.
//
// Ce qui fait un chrome, c'est le CONTRASTE : ~40 % de la boule dans le
// quasi-noir (0,03, teinté de la couleur la plus sombre de la pochette) - le
// bas reflète un sol noir sous une ligne d'horizon, le côté droit tourne le
// dos à la lumière -, deux boîtes à lumière LARGES (rectangles arrondis de
// 15 % et 8 % du diamètre de haut, bords doux sur 3 px de matcap, 1,1 de
// luminance : les seules choses qui fleurissent), et un liseré de Fresnel
// coloré plafonné à 0,5. Une version précédente était pastel et sans
// ombres : des objets plats, et un voile de bloom gris autour de chacun.
//
// L'ENCODAGE : 8 bits ne montent pas à 1,1, et 0,03 en octets linéaires
// tombe sur 8/255 - des marches dans les ombres. La texture porte donc
// sqrt(v / MATCAP_GAIN) et le shader rend c * c * MATCAP_GAIN : une seule
// lecture, la plage jusqu'à 1,25, deux fois plus de pas dans les noirs.
//
// LE PLAFOND DU CORPS : le shader fait tourner la teinte (rotation autour de
// l'axe gris), ce qui CHANGE la luminance d'une couleur saturée (un bleu à
// 0,1 devient un vert à 0,5). Le corps est donc plafonné sur la luminance
// MAXIMALE qu'il atteint sous toutes les rotations (0,6 pour le corps, 0,5
// pour le liseré), pas sur sa luminance du moment : aucune teinte ne le
// fait passer au-dessus du seuil de bloom (0,78).
// Le studio (ombres, boîtes, encodage, plafond sous rotation de teinte) vit
// dans lib/matcap.js, partagé avec Ribbons.
export { MATCAP_GAIN, lumMaxSousRotation };

// colours : les couleurs vives de la pochette (déjà saturées par vdjColor),
// en linéaire. Rend { data (encodé), lin (Float32, linéaire), size, kind }.
// opts (optional, Totem passes none and gets exactly the matcaps above):
//   edgePx   the light boxes' soft edge, in matcap pixels (studio's default 1.5)
//   hueSpan  the iridescent bands' hue sweep, in turns per disc unit (1.1)
// Bloom passes softer boxes and a narrower sweep: on a petal the boxes'
// hard edge followed the triangles, and a full rainbow per flower made
// every flower the same colour wheel.
export function genererMatcap(kind, colours, size, opts) {
  const n = size || MATCAP_SIZE;
  const o = opts || {};
  const studioOpts = o.edgePx != null ? { edgePx: o.edgePx } : undefined;
  const hueSpan = o.hueSpan != null ? o.hueSpan : 1.1;
  const data = new Uint8Array(n * n * 4), lin = new Float32Array(n * n * 3);
  const cA = colours[0], cB = colours[1] || colours[0];
  const hslA = { h: 0, s: 0, l: 0 }; cA.getHSL(hslA);
  const hslB = { h: 0, s: 0, l: 0 }; cB.getHSL(hslB);
  // la couleur la plus sombre de la palette teinte les ombres
  // (0,03 sur le canal le PLUS FORT, pas sur la luminance : un rose à 0,03
  // de luminance porte 0,1 de rouge et se lit bordeaux, pas noir)
  let dark = colours[0], dl = 9;
  for (const c of colours) { const l = LUMW[0] * c.r + LUMW[1] * c.g + LUMW[2] * c.b; if (l < dl) { dl = l; dark = c; } }
  const dk = MATCAP_DARK / Math.max(1e-4, dark.r, dark.g, dark.b);
  const darkRGB = [dark.r * dk, dark.g * dk, dark.b * dk];
  const rimRGB = [cA.r, cA.g, cA.b];
  const px = 2 / n;                                 // un pixel de matcap en unités du disque
  const tmp = [0, 0, 0];
  for (let j = 0; j < n; j++) {
    const y = ((j + 0.5) / n) * 2 - 1;
    for (let i = 0; i < n; i++) {
      const x = ((i + 0.5) / n) * 2 - 1;
      // le studio partagé : l'ombre (sol sous l'horizon, côté droit), les
      // boîtes à lumière (bords doux sur 3 px), le Fresnel - lib/matcap.js
      const st = studio(x, y, px, studioOpts);
      const nz = st.nz, ry = st.ry, lit = st.lit, fres = st.fres, box = st.box;
      let body;                                                    // [r, g, b] du corps éclairé
      if (kind === 'chrome') {
        // le ciel du studio : gris froid, plus clair vers l'horizon
        const v = 0.12 + 0.18 * smooth01(0.9, 0.05, ry);
        body = [v * 0.97, v, v * 1.04];
      } else if (kind === 'iridescent') {
        // un film mince : les bandes suivent les COURBES DE NIVEAU autour du
        // centre (la distance au centre, décentrée vers la lumière), saturées
        const rc = Math.hypot(x + 0.12, y - 0.14);
        const h = hslA.h + rc * hueSpan;
        body = teinte(h, 0.9, 0.34 + 0.12 * smooth01(1.0, 0.2, rc), tmp).slice();
      } else {
        // néon : A au ciel, B près de l'horizon, une frontière franche
        const m = smooth01(0.30, 0.42, ry);
        const a = teinte(hslA.h, 0.95, 0.42, tmp).slice(), b = teinte(hslB.h, 0.95, 0.30, tmp).slice();
        body = [b[0] + (a[0] - b[0]) * m, b[1] + (a[1] - b[1]) * m, b[2] + (a[2] - b[2]) * m];
      }
      // le corps plafonné sous toutes les rotations de teinte
      const bm = lumMaxSousRotation(body[0], body[1], body[2]);
      if (bm > BODY_MAX) { const k = BODY_MAX / bm; body[0] *= k; body[1] *= k; body[2] *= k; }
      let r = darkRGB[0] + (body[0] - darkRGB[0]) * lit, g = darkRGB[1] + (body[1] - darkRGB[1]) * lit, b = darkRGB[2] + (body[2] - darkRGB[2]) * lit;
      // le LISERÉ de Fresnel, coloré, plafonné à 0,5 (sous le seuil de bloom)
      const rimM = lumMaxSousRotation(rimRGB[0], rimRGB[1], rimRGB[2]);
      // un MÉLANGE convexe vers le liseré (à RIM_MAX), pas un max canal par
      // canal : la luminance maximale sous rotation est convexe, le mélange
      // reste sous le plus haut des deux plafonds - le max par canal d'un
      // orange et d'un rose les dépassait tous les deux (0,69)
      const rk = RIM_MAX / Math.max(1e-4, rimM), w = Math.min(1, fres * 1.6);
      r += (rimRGB[0] * rk - r) * w; g += (rimRGB[1] * rk - g) * w; b += (rimRGB[2] * rk - b) * w;
      // les deux BOÎTES À LUMIÈRE, bords doux sur 3 px
      r += (BOX_LUM - r) * box; g += (BOX_LUM - g) * box; b += (BOX_LUM - b) * box;
      const q = j * n + i, o = q * 4;
      lin[q * 3] = r; lin[q * 3 + 1] = g; lin[q * 3 + 2] = b;
      data[o] = Math.round(Math.sqrt(clamp01(r / MATCAP_GAIN)) * 255);
      data[o + 1] = Math.round(Math.sqrt(clamp01(g / MATCAP_GAIN)) * 255);
      data[o + 2] = Math.round(Math.sqrt(clamp01(b / MATCAP_GAIN)) * 255);
      data[o + 3] = 255;
    }
  }
  return { data, lin, size: n, kind };
}

function matcapTexture(m) {
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.needsUpdate = true;
  return t;
}

// ------------------------------------------------------------------ shaders

const SCULPTURE_VERTEX = `
  precision highp float;
  attribute vec3 aSphere;
  attribute vec2 aParam;
  uniform float uShape;
  uniform float uMorph;
  uniform float uTime;
  uniform float uTwist;
  uniform float uSpike;
  uniform float uBlob;
  uniform float uSphereR;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocal;

  // le tore vrillé : section quadrilobée dont les lobes tournent le long de
  // l'anneau (trois quarts de tour par tour, pour se refermer) et avancent
  vec3 twistPos(float th, float ph) {
    float a = ph + 0.75 * th + uTwist;
    float r = 0.27 * (1.0 + 0.38 * cos(4.0 * a));
    vec3 c = vec3(cos(th), 0.0, sin(th)) * 0.85;
    return c + (c * cos(ph) + vec3(0.0, 1.0, 0.0) * sin(ph)) * r;
  }
  vec3 rotY(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z); }

  void main() {
    vec3 p = position;
    vec3 n = normal;
    if (uShape > 0.5 && uShape < 1.5) {
      p = twistPos(aParam.x, aParam.y);
      vec3 du = twistPos(aParam.x + 0.01, aParam.y) - twistPos(aParam.x - 0.01, aParam.y);
      vec3 dv = twistPos(aParam.x, aParam.y + 0.01) - twistPos(aParam.x, aParam.y - 0.01);
      n = normalize(cross(dv, du));   // du x dv pointe vers l'INTÉRIEUR du tube (dv vaut +y à phi = 0) : un tore plat, couleur du liseré
    } else if (uShape > 1.5 && uShape < 2.5) {
      float a = uTime * (aParam.x - 2.0) * 0.45;
      p = rotY(p, a); n = rotY(n, a);
    } else if (uShape > 2.5 && uShape < 3.5) {
      vec3 s = aSphere;
      vec3 ph = vec3(3.1 * s.x + uTime * 1.3, 2.7 * s.y + uTime * 1.0 + 1.7, 3.4 * s.z + uTime * 0.8 + 3.1);
      float d = (sin(ph.x) + sin(ph.y) + sin(ph.z)) / 3.0;
      vec3 g = vec3(3.1 * cos(ph.x), 2.7 * cos(ph.y), 3.4 * cos(ph.z)) / 3.0;
      float r = 1.0 + uBlob * d;
      p = s * r;
      n = normalize(s * r - uBlob * (g - dot(g, s) * s));
    } else if (uShape > 3.5) {
      p += n * (aParam.x * uSpike);
    }
    p = mix(p, aSphere * uSphereR, uMorph);
    n = normalize(mix(n, aSphere, uMorph));
    vLocal = p;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vView = mv.xyz;
    vNormal = normalize(normalMatrix * n);
    gl_Position = projectionMatrix * mv;
  }
`;

export const SCULPTURE_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uMatcap;
  uniform float uHue;
  uniform float uHueGrad;
  uniform float uExposure;
  uniform highp float uShape;               // déclaré highp côté sommet : même précision ou le programme ne valide pas
  uniform float uFacet;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vLocal;

  // rotation de teinte : une rotation RGB autour de l'axe gris, le blanc
  // reste blanc, le gris reste gris
  vec3 hueShift(vec3 c, float h) {
    const vec3 k = vec3(0.57735027);
    float cs = cos(h), sn = sin(h);
    return max(c * cs + cross(k, c) * sn + k * dot(k, c) * (1.0 - cs), 0.0);
  }

  void main() {
    vec3 n = normalize(vNormal);
    if (uShape > 3.5) {
      // les pointes : à facettes, par les dérivées, dès qu'elles sortent
      vec3 fn = normalize(cross(dFdx(vView), dFdy(vView)));
      n = normalize(mix(n, fn, uFacet));
    }
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.0) + vec3(0.0, 0.0, 0.02));
    vec2 muv = n.xy * 0.49 + 0.5;
    vec3 c = texture2D(uMatcap, muv).rgb;
    c = c * c * ${MATCAP_GAIN.toFixed(2)};          // la texture porte sqrt(v / gain)
    c = hueShift(c, (uHue + uHueGrad * vLocal.y) * 6.2831853);
    gl_FragColor = vec4(c * uExposure, 1.0);
  }
`;

const QUAD_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const BG_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uMirror;
  uniform float uAspect;
  uniform float uZoom;
  uniform float uDark;
  uniform float uRot;
  uniform float uShift;
  uniform vec2 uTexel;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${BG_SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    // la rosace centrée sur la pochette (uShift) et tournée lentement (uRot)
    // AVANT le pli : c'est le motif entier qui tourne
    vec2 d = (vUv - vec2(0.5, 0.5 + uShift)) * vec2(uAspect, 1.0);
    float cr = cos(uRot), sr = sin(uRot);
    d = vec2(cr * d.x - sr * d.y, sr * d.x + cr * d.y);
    vec2 uv = vdjMirror(d / vec2(uAspect, 1.0) + 0.5, uMirror, uAspect);
    uv = (uv - 0.5) / uZoom + 0.5;
    // quatre lectures en grille tournée : la cible réduite est agrandie, un
    // bord dur y ferait des marches
    // quatre lectures à un demi-texel : chacune moyenne 2 x 2 texels, la
    // somme est un filtre en tente 3 x 3 - un bord dur agrandi ne fait ni
    // marches ni pointillés (des décalages plus larges faisaient un moiré
    // sur les bandes blanches fines)
    vec3 c = texture2D(uTex, uv + uTexel * vec2(0.5, 0.5)).rgb + texture2D(uTex, uv + uTexel * vec2(-0.5, 0.5)).rgb
           + texture2D(uTex, uv + uTexel * vec2(-0.5, -0.5)).rgb + texture2D(uTex, uv + uTexel * vec2(0.5, -0.5)).rgb;
    gl_FragColor = vec4(c * 0.25 * uDark * safeAtt(vUv), 1.0);
  }
`;

const MATCAP_VIEW_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uA; uniform sampler2D uB; uniform sampler2D uC;
  uniform float uAspect;
  varying vec2 vUv;
  void main() {
    // trois disques côte à côte, sur fond noir
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0) * 1.1;
    vec3 c = vec3(0.0);
    for (int k = 0; k < 3; k++) {
      vec2 q = (p - vec2(float(k - 1) * 0.68, 0.0)) / 0.31;
      if (dot(q, q) < 1.0) {
        vec2 uv = q * 0.5 + 0.5;
        c = k == 0 ? texture2D(uA, uv).rgb : k == 1 ? texture2D(uB, uv).rgb : texture2D(uC, uv).rgb;
        c = c * c * ${MATCAP_GAIN.toFixed(2)};
      }
    }
    gl_FragColor = vec4(c, 1.0);
  }
`;

// Les matériaux vivent au niveau du module : sur le Pi, un shader de cette
// taille se compile en secondes et serait recompilé à chaque apparition.
const MATERIALS = [];
function sculptureMaterial(slot) {
  if (MATERIALS[slot]) return MATERIALS[slot];
  const m = new THREE.ShaderMaterial({
    uniforms: {
      uMatcap: { value: null }, uHue: { value: 0 }, uHueGrad: { value: HUE_GRAD }, uExposure: { value: EXPOSURE },
      uShape: { value: 0 }, uMorph: { value: 0 }, uTime: { value: 0 }, uTwist: { value: 0 }, uSpike: { value: 0 }, uBlob: { value: 0.3 },
      uSphereR: { value: SPHERE_R }, uFacet: { value: 0 }
    },
    vertexShader: SCULPTURE_VERTEX, fragmentShader: SCULPTURE_FRAGMENT, side: THREE.FrontSide
  });
  MATERIALS[slot] = m;
  return m;
}
let BG_MATERIAL = null, VIEW_MATERIAL = null;

// ------------------------------------------------------------- la composition

// L'ORBITE : une ellipse x = A cos t, y = B sin t, z = -D sin t (le haut passe
// derrière, le bas devant) et l'échelle s des sculptures. Choisies pour que
// CHAQUE sculpture reste ENTIÈRE dans le cadre avec 5 % de marge, mesuré par
// projection avec la caméra de référence (reinitialiserCamera : fov 68,
// (0, 1,4, 6) visant (0, 1, 0)) : centre projeté + rayon projeté
// R / (distance x tan(fov / 2)). Pour chaque échelle (de 1 à 0,4), la plus
// grande ellipse du cadre - B d'abord (le haut et le bas), puis A - et on
// garde la première dont aucune sculpture ne couvre plus de la moitié de son
// diamètre du cartouche de la pochette. Sur un écran très large (le kiosque,
// 3,2:1), c'est ce critère qui réduit les sculptures.
const _cam = new THREE.PerspectiveCamera(68, 1.78, 0.1, 100);
const _v = new THREE.Vector3();
export function orbitePoint(O, th, front) {
  return { x: O.A * Math.cos(th), y: GROUP_Y + O.B * Math.sin(th), z: front ? 0 : -ORBIT_DEPTH * Math.sin(th), local: true };
}
function projeter(x, y, z, R, aspect) {
  // le groupe est grossi de l'à-coup (+4 %) autour de son centre
  const k = KICK_ZOOM_MAX;
  _v.set(x * k, GROUP_Y + (y - GROUP_Y) * k, z * k);
  const dist = _v.distanceTo(_cam.position);
  _v.project(_cam);
  const r = (R * k) / (dist * Math.tan(THREE.MathUtils.degToRad(_cam.fov / 2)));
  return { x: _v.x, y: _v.y, r };
}
export function orbite(aspect, safe, n, samples) {
  _cam.aspect = aspect; _cam.fov = 68; _cam.position.set(0, 1.4, 6); _cam.up.set(0, 1, 0); _cam.lookAt(0, 1, 0);
  _cam.updateProjectionMatrix(); _cam.updateMatrixWorld(true);
  const N = samples || 48, lim = 1 - MARGIN_NDC - KICK_NDC;
  const ths = []; for (let k = 0; k < N; k++) ths.push(k / N * Math.PI * 2);
  const fronts = []; for (let i = 0; i < (n || 3); i++) fronts.push(Math.PI / 2 + i * Math.PI * 2 / (n || 3));
  const pts = (O) => ths.map((t) => [orbitePoint(O, t, false), t]).concat(fronts.map((t) => [orbitePoint(O, t, true), t]));
  const dansCadre = (O, R) => pts(O).every(([p]) => { const q = projeter(p.x, p.y, p.z, R, aspect); return Math.abs(q.y) + q.r <= lim && Math.abs(q.x) + q.r / aspect <= 1 - (MARGIN_NDC + KICK_NDC) / aspect; });
  // la part du diamètre qui mord sur le cartouche, au pire (0 = jamais)
  const morsure = (O, R) => {
    let worst = 0;
    for (const [p] of pts(O)) {
      const q = projeter(p.x, p.y, p.z, R, aspect);
      const sx = q.x * aspect, sy = q.y;
      const qx = Math.abs(sx - safe.cx) - safe.hw, qy = Math.abs(sy - safe.cy) - safe.hh;
      const d = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
      worst = Math.max(worst, clamp01((q.r - d) / (2 * q.r)));
    }
    return worst;
  };
  const cherche = (lo, hi, ok) => { if (!ok(lo)) return lo; for (let k = 0; k < 24; k++) { const m = (lo + hi) / 2; if (ok(m)) lo = m; else hi = m; } return lo; };
  let best = null;
  for (let s = 1; s >= 0.399; s -= 0.05) {
    const R = OBJ_R * MESH_SCALE * s;
    const B = cherche(0, 6, (b) => dansCadre({ A: 0, B: b }, R));
    const A = cherche(0, 14, (a) => dansCadre({ A: a, B }, R));
    const O = { A, B, s: +s.toFixed(2), bite: 0, fits: dansCadre({ A, B }, R) };
    O.bite = morsure(O, R);
    if (!best || O.bite < best.bite - 1e-3) best = O;
    if (O.fits && O.bite <= 0.5) return O;
  }
  return best;
}

// ------------------------------------------------------------- le visualiseur

export class TotemVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layout = TIER[this._tier];
    const fs = urlParam('totemshape');
    this._forcedShape = fs && SHAPES.includes(fs === 'knot' ? 'knot23' : fs) ? (fs === 'knot' ? 'knot23' : fs) : null;
    const fm = urlParam('totemmatcap');
    this._forcedMatcap = fm && MATCAPS.includes(fm) ? fm : null;
    this._matcapView = urlParam('totemmatcapview') === '1';
    this._probe = urlParam('totemprobe') === '1';
    // bloom 0,35 : les boîtes à lumière sont LARGES ; à 0,6 leur halo laissait
    // un voile gris autour de chaque sculpture (médiane du fond 4/255)
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.015, vignette: 0.45 });
    this._comfort = new VdjComfort();
    this._seed = 101;
  }

  get postProcessing() { return this._post.value; }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const L = this._layout;
    this._group = new THREE.Group();
    this._group.position.set(0, GROUP_Y, 0);          // le centre de la pochette (0,0855 x 4,05 au-dessus du centre)
    scene.add(this._group);
    this._meshes = [];
    for (let i = 0; i < L.sculptures; i++) {
      const m = new THREE.Mesh(geometrie(SHAPES[i]), sculptureMaterial(i));
      m.frustumCulled = false;
      m.scale.setScalar(MESH_SCALE);
      m.userData.shape = SHAPES[i];
      m.material.uniforms.uShape.value = SHAPE_ID[SHAPES[i]];
      m.material.uniforms.uMorph.value = 0;
      this._group.add(m); this._meshes.push(m);
    }
    // le fond kaléidoscopique : les sculptures rendues dans une cible réduite
    this._bgTarget = null; this._bgScale = L.bgScale;
    if (L.bgScale > 0 && !this._matcapView) {
      if (!BG_MATERIAL) {
        BG_MATERIAL = new THREE.ShaderMaterial({
          uniforms: { uTex: { value: null }, uMirror: { value: 3 }, uAspect: { value: 1.78 }, uZoom: { value: BG_ZOOM }, uDark: { value: BG_DARK }, uRot: { value: 0 }, uShift: { value: 0 }, uTexel: { value: new THREE.Vector2(1 / 640, 1 / 360) }, uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather },
          vertexShader: QUAD_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false
        });
      }
      this._bgU = BG_MATERIAL.uniforms;
      this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), BG_MATERIAL);
      this._bg.frustumCulled = false; this._bg.renderOrder = -1000;
      const self = this;
      this._bg.onBeforeRender = function (renderer, sc, cam) { self._renderBackground(renderer, cam); };
      scene.add(this._bg);
    } else this._bg = null;
    if (this._matcapView) {
      if (!VIEW_MATERIAL) VIEW_MATERIAL = new THREE.ShaderMaterial({ uniforms: { uA: { value: null }, uB: { value: null }, uC: { value: null }, uAspect: { value: 1.78 } }, vertexShader: QUAD_VERTEX, fragmentShader: MATCAP_VIEW_FRAGMENT, depthTest: false, depthWrite: false });
      this._view = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), VIEW_MATERIAL);
      this._view.frustumCulled = false; this._view.renderOrder = 1000;
      scene.add(this._view);
      for (const m of this._meshes) m.visible = false;
    }
    this._textures = { chrome: null, iridescent: null, neon: null };
    this._paletteSeen = undefined;
    this._matcapIdx = this._forcedMatcap ? MATCAPS.indexOf(this._forcedMatcap) : 0;
    this._applyPalette({ artworkPalette: null });

    // l'état musical
    this._time = 0; this._paused = false; this._frame = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._bars = 0; this._secondsBeat = 0.5;
    this._beatCont = 0; this._spinExtra = 0; this._orbitExtra = 0; this._orbitDir = 1; this._orbitOffset = 0;
    this._low = 0.5; this._mid = 0.4; this._high = 0.4; this._buildup = 0;
    this._beatAge = 10; this._barAge = 10; this._hue = 0; this._twist = 0;
    this._morphAt = -1; this._morphSwapped = true; this._dropUntil = -1;
    this._bgRotJump = 0; this._shapeCursor = 0; this._orbitKey = null; this._orbit = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._lumAvg = 0; this._gain = 1; this._lumPixels = null;
    this._hideForeground = false; this._freeze = false;
    if (this._forcedShape) for (const m of this._meshes) this._setShape(m, this._forcedShape);
    if (this._probe && typeof window !== 'undefined') window.__totem = this;
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bffea];
    // comparée par CONTENU, pas par identité : trois matcaps de 256 x 256 à
    // régénérer coûtent 100 ms sur le Pi, et un profil synthétique (banc,
    // sonde) fournit un objet neuf à chaque image
    const key = String(v[0]) + '|' + String(v[1 % v.length]) + '|' + String(v[2 % v.length]);
    if (key === this._paletteSeen && this._textures.chrome) return;
    this._paletteSeen = key;
    const colours = [vdjColor(v[0]), vdjColor(v[1 % v.length]), vdjColor(v[2 % v.length])];
    for (const k of MATCAPS) {
      if (this._textures[k]) this._textures[k].dispose();
      this._textures[k] = matcapTexture(genererMatcap(k, colours, MATCAP_SIZE));
    }
    this._setMatcap(this._matcapIdx);
    if (this._view) { const U = VIEW_MATERIAL.uniforms; U.uA.value = this._textures.chrome; U.uB.value = this._textures.iridescent; U.uC.value = this._textures.neon; }
  }

  _setMatcap(idx) {
    this._matcapIdx = ((idx % MATCAPS.length) + MATCAPS.length) % MATCAPS.length;
    const t = this._textures[MATCAPS[this._matcapIdx]];
    for (const m of this._meshes) m.material.uniforms.uMatcap.value = t;
  }

  _setShape(mesh, shape) {
    mesh.geometry = geometrie(shape);
    mesh.userData.shape = shape;
    mesh.material.uniforms.uShape.value = SHAPE_ID[shape];
  }

  // la prochaine forme d'un socle : différente de la sienne et de celles des autres
  _nextShape(mesh) {
    if (this._forcedShape) return this._forcedShape;
    const taken = new Set(this._meshes.map((m) => m.userData.next || m.userData.shape));
    for (let k = 0; k < SHAPES.length; k++) {
      const s = SHAPES[(this._shapeCursor + k) % SHAPES.length];
      if (!taken.has(s) && s !== mesh.userData.shape) { this._shapeCursor = (this._shapeCursor + k + 1) % SHAPES.length; return s; }
    }
    return SHAPES[(SHAPES.indexOf(mesh.userData.shape) + 1) % SHAPES.length];
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    if (this._freeze) return;
    this._paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.1);
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low = clamp01(bands.low); this._mid = clamp01(bands.mid); this._high = clamp01(bands.high); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const beatS = this._secondsBeat, barS = 4 * beatS;

    if (this._paused) {
      // pause : la rotation à 10 %, rien d'autre ne bouge
      this._time += dt * PAUSE_RATE;
      this._spinExtra += dt * PAUSE_RATE * 0.5;
      this._place(0, 0);
      this._post.update(1);
      return;
    }
    this._time += dt;
    this._frame++;

    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    const beatContNew = bp != null ? this._beats + bp : this._beats + this._free / beatS;
    // un saut d'horloge (seek, resynchro) ne doit pas faire sauter l'orbite
    if (Math.abs(beatContNew - this._beatCont) > 2) this._orbitOffset += (this._beatCont - beatContNew) * this._orbitDir / ORBIT_BEATS;
    this._beatCont = beatContNew;

    // la montée : un drop connu qui approche, ou le segment « build »
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 1.0));

    // DROP : flash, triangle devant, pointes, rotation rapide pendant 4 mesures
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid && this._time - this._dropUntil > barS) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop) {
      this._dropUntil = this._time + DROP_BARS * barS;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
      for (const m of this._meshes) { m.userData.next = null; this._setShape(m, this._forcedShape || 'spikes'); m.material.uniforms.uMorph.value = 0; }
      this._morphAt = -1; this._morphSwapped = true;
      this._beatAge = 0;
    }
    const inDrop = this._time < this._dropUntil;

    // TEMPS : saut d'échelle x1,12, les pointes sortent
    if (beat) this._beatAge = 0;
    this._beatAge += dt;
    // MESURE : le morph (contraction en un demi-temps, échange, expansion), l'à-coup de caméra
    if (bar) { this._barAge = 0; if (!inDrop) { this._morphAt = this._time; this._morphSwapped = false; for (const m of this._meshes) m.userData.next = this._nextShape(m); } }
    this._barAge += dt;
    if (this._morphAt >= 0) {
      const half = 0.5 * beatS, u = (this._time - this._morphAt) / half;
      let morph;
      if (u < 1) morph = u * u * (3 - 2 * u);
      else if (!this._morphSwapped) { for (const m of this._meshes) { if (m.userData.next) this._setShape(m, m.userData.next); m.userData.next = null; } this._morphSwapped = true; morph = 1; }
      else if (u < 2) { const w = 2 - u; morph = w * w * (3 - 2 * w); }
      else { morph = 0; this._morphAt = -1; }
      for (const m of this._meshes) m.material.uniforms.uMorph.value = morph;
    }
    // PHRASE : matcap suivante, orbite inversée, miroir suivant - en coupe
    if (phrase) {
      if (!this._forcedMatcap) this._setMatcap(this._matcapIdx + 1);
      // inversion continue : l'angle ne saute pas
      this._orbitOffset += 2 * this._orbitDir * vdjLoop(this._beatCont, ORBIT_BEATS);
      this._orbitDir = -this._orbitDir;
      this._bgRotJump += BG_PHRASE_TURN;
    }

    // les vitesses modulées : la rotation propre s'emballe en montée et sur le drop
    const spinRate = (inDrop ? DROP_SPIN : 0) + RISE_SPIN * this._buildup;
    this._spinExtra += dt / beatS * spinRate;
    this._orbitExtra += dt / beatS * (this._mid - 0.4) * 0.03 * this._orbitDir;
    // bornés : mediump est du fp16 sur la télé, un angle qui grandit sans fin y saute
    this._hue = (this._hue + dt * (HUE_BASE + HUE_HIGH * this._high)) % 1;
    this._twist = (this._twist + dt * (0.6 + 1.2 * this._mid)) % (Math.PI * 2);
    this._spinExtra %= 1;

    const kick = vdjCameraKick(barPhase != null ? barPhase : this._barAge / barS, this._bars, 4);
    this._place(kick.zoom, kick.roll);

    // le confort : la mesure de la cible de fond (tiers 1-2), une estimation sinon
    if (this._bgTarget && this._lumMeasured != null) { this._gain = this._comfort.measure(this._lumMeasured, dt * LUM_EVERY); this._lumMeasured = null; }
    else if (!this._bgTarget) this._gain = this._comfort.measure(0.08, dt);
    for (const m of this._meshes) m.material.uniforms.uExposure.value = EXPOSURE * this._gain;
    // la séparation RGB sur le temps : l'aberration de la passe filmic
    this._post.update(vdjBeatChroma(bp != null ? bp : this._free / beatS, beatS));
  }

  // Les positions, rotations, uniformes de cette image.
  _place(zoom, roll) {
    const n = this._meshes.length;
    const inDrop = this._time < this._dropUntil;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    // l'orbite recalculée quand l'écran ou le cartouche change de forme
    // R19 : l'orbite et la rosace autour du centre de scène, pas de la pochette
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const key = [aspect, sc.x, sc.y, sh.x, sh.y, n].map((v) => Math.round(v * 200)).join(',');
    if (key !== this._orbitKey) {
      this._orbitKey = key;
      this._orbit = orbite(aspect, { cx: sc.x, cy: sc.y, hw: sh.x, hh: sh.y }, n);
    }
    const O = this._orbit;
    const theta0 = (vdjLoop(this._beatCont, ORBIT_BEATS) * this._orbitDir + this._orbitOffset + this._orbitExtra) * Math.PI * 2;
    const jump = 1 + BEAT_JUMP * (0.6 + 0.6 * this._low) * Math.exp(-this._beatAge / BEAT_TAU_S);
    const spike = Math.min(1, Math.exp(-this._beatAge / SPIKE_TAU_S) * (0.5 + 0.8 * this._low) * (inDrop ? 1.3 : 1));
    const spin = vdjLoop(this._beatCont, SPIN_BEATS) * Math.PI * 2 + this._spinExtra * Math.PI * 2;
    const wobble = Math.sin(vdjLoop(this._beatCont, WOBBLE_BEATS) * Math.PI * 2) * 0.45;
    for (let i = 0; i < n; i++) {
      const m = this._meshes[i], U = m.material.uniforms;
      // sur le drop : le triangle (ou la paire), dans le plan frontal, aux
      // points de l'orbite que orbite() a vérifiés eux aussi
      const th = inDrop ? Math.PI / 2 + i * Math.PI * 2 / n : theta0 + i * Math.PI * 2 / n;
      const p = orbitePoint(O, th, inDrop);
      m.position.set(p.x, p.y, p.z);
      m.rotation.set(wobble * (i % 2 ? -1 : 1), spin * (1 + 0.15 * i) * (i % 2 ? -1 : 1), 0.3 * i);
      m.scale.setScalar(MESH_SCALE * O.s * jump);
      U.uTime.value = this._time;
      U.uTwist.value = this._twist;
      U.uSpike.value = SPIKE_LEN * spike;
      U.uFacet.value = clamp01(spike * 3);
      U.uBlob.value = 0.18 + 0.32 * this._low;
      U.uHue.value = (this._hue + i * 0.17) % 1;
    }
    this._group.scale.setScalar(zoom || 1);
    this._group.rotation.z = roll || 0;
    if (this._bg) {
      this._bgU.uMirror.value = 3;
      this._bgU.uAspect.value = aspect;
      this._bgU.uDark.value = BG_DARK * this._gain;
      this._bgU.uShift.value = sc.y / 2;
      this._bgU.uRot.value = vdjLoop(this._beatCont, BG_TURN_BEATS) * Math.PI * 2 + this._bgRotJump;
    }
    if (this._view) VIEW_MATERIAL.uniforms.uAspect.value = aspect;
  }


  _renderBackground(renderer, camera) {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(8, Math.round(size.x * this._bgScale)), h = Math.max(8, Math.round(size.y * this._bgScale));
    if (!this._bgTarget || this._bgTarget.width !== w || this._bgTarget.height !== h) {
      if (this._bgTarget) this._bgTarget.dispose();
      // 4x MSAA : la cible en demi-résolution est agrandie ~3 fois (x2 puis
      // le zoom 1,6) ; sans anticrénelage les bords de la rosace font des marches
      this._bgTarget = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: true, type: THREE.UnsignedByteType, wrapS: THREE.MirroredRepeatWrapping, wrapT: THREE.MirroredRepeatWrapping, samples: 4 });
      this._lumPixels = new Uint8Array(w * h * 4);
      this._bgU.uTexel.value.set(1 / w, 1 / h);
    }
    // la passe du fond : les sculptures resserrées autour du centre de la
    // cible (là où le kaléidoscope lit), agrandies, dans le plan frontal -
    // voir BG_R. Le groupe descend sur la visée de la caméra (y = 1) pour que
    // son centre tombe au centre de la cible ; uShift remonte la rosace sur
    // la pochette.
    const saved = this._meshes.map((m) => [m.position.clone(), m.scale.x]);
    const gy = this._group.position.y, gs = this._group.scale.x, gr = this._group.rotation.z;
    this._group.position.y = 1.0; this._group.scale.setScalar(1); this._group.rotation.z = 0;
    const nb = this._meshes.length, phase = vdjLoop(this._beatCont, ORBIT_BEATS) * Math.PI * 2 * this._orbitDir;
    const rb = BG_R + BG_R_SWING * Math.sin(vdjLoop(this._beatCont, 8) * Math.PI * 2);
    for (let i = 0; i < nb; i++) {
      const m = this._meshes[i], a = phase + i * Math.PI * 2 / nb;
      m.position.set(rb * Math.cos(a), rb * Math.sin(a), 0);
      m.scale.setScalar(saved[i][1] / Math.max(1e-3, this._orbit ? this._orbit.s : 1) * BG_S);
    }
    this._group.updateMatrixWorld(true);
    const prev = renderer.getRenderTarget();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    const clearPrev = renderer.getClearColor(new THREE.Color()), alphaPrev = renderer.getClearAlpha();
    const bgVisible = this._bg.visible; this._bg.visible = false;
    const viewVisible = this._view ? this._view.visible : false; if (this._view) this._view.visible = false;
    for (const m of this._meshes) m.visible = true;
    renderer.setRenderTarget(this._bgTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._group, camera);
    // le confort : la luminance moyenne des sculptures, lue toutes les 30 images
    if (this._frame % LUM_EVERY === 0 && this._lumPixels) {
      renderer.readRenderTargetPixels(this._bgTarget, 0, 0, w, h, this._lumPixels);
      let s = 0; const px = this._lumPixels, step = 4 * 7;
      let c = 0;
      for (let i = 0; i < px.length; i += step) { s += (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114); c++; }
      // la grappe du fond couvre à peu près ce que la rosace couvre (à 35 %),
      // et les sculptures du premier plan moins : une estimation prudente
      this._lumMeasured = (s / c / 255) * (BG_DARK + 0.6);
    }
    // TOUT remettre AVANT de recalculer les matrices : la passe principale
    // qui suit lit matrixWorld tel quel (un updateMatrixWorld placé avant la
    // remise des positions dessinait la grappe du fond au premier plan)
    for (let i = 0; i < nb; i++) { this._meshes[i].position.copy(saved[i][0]); this._meshes[i].scale.setScalar(saved[i][1]); }
    this._group.position.y = gy;
    this._group.scale.setScalar(gs);
    this._group.rotation.z = gr;
    this._group.updateMatrixWorld(true);
    renderer.setRenderTarget(prev);
    renderer.setClearColor(clearPrev, alphaPrev);
    renderer.xr.enabled = xr;
    this._bg.visible = bgVisible;
    if (this._view) this._view.visible = viewVisible;
    if (this._hideForeground) for (const m of this._meshes) m.visible = false;
    this._bgU.uTex.value = this._bgTarget.texture;
  }

  getProbe() {
    return {
      tier: this._tier, shapes: this._meshes.map((m) => m.userData.shape), matcap: MATCAPS[this._matcapIdx], safe: { aspect: safeZoneUniforms.uAspect.value, c: safeZoneUniforms.uSafeCenter.value.toArray(), h: safeZoneUniforms.uSafeHalfSize.value.toArray() }, mirror: 3, bgRot: this._bgU ? this._bgU.uRot.value : 0, bgShift: this._bgU ? this._bgU.uShift.value : 0, orbit: this._orbit,
      morph: this._meshes[0].material.uniforms.uMorph.value, orbitDir: this._orbitDir, hue: this._hue, lumAvg: this._comfort.average, gain: this._gain,
      inDrop: this._time < this._dropUntil, beats: this._beats, bars: this._bars, bg: !!this._bgTarget,
      aberration: this._post.value.aberration
    };
  }

  dispose() {
    if (this._scene) {
      this._scene.remove(this._group);
      if (this._bg) { this._bg.onBeforeRender = null; this._scene.remove(this._bg); this._bg.geometry.dispose(); }
      if (this._view) { this._scene.remove(this._view); this._view.geometry.dispose(); }
    }
    if (this._bgTarget) { this._bgTarget.dispose(); this._bgTarget = null; }
    for (const k of MATCAPS) if (this._textures && this._textures[k]) { this._textures[k].dispose(); this._textures[k] = null; }
    for (const m of this._meshes || []) m.material.uniforms.uMatcap.value = null;
    if (this._bgU) this._bgU.uTex.value = null;
    if (typeof window !== 'undefined' && window.__totem === this) window.__totem = null;
    this._scene = null; this._meshes = [];
  }
}
